import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  Chip,
  Typography,
  Button,
  Grid,
  Container,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Dialog,
  DialogContent,
  TextField,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoricalTransactionsSection from "./HistoricalTransactionsSection";
import {
  getSpecificTransaction,
  setTransactionDecision,
} from "../../../../services/transactions";
import TableSkeleton from "../../../../components/skeletons/TableSkeleton";
import { isMobileApp } from "../../../../utils/helpers";
import { useNavigate } from "react-router-dom";

const useTransactionUpdate = (token) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const updateTransaction = useCallback(
    async (
      transactionUuid,
      decision,
      inspectedPhysicalPassport,
      reasonForInspectedPhysicalPassport,
      riskAssessmentResult
    ) => {
      // if (!token) {
      //   console.log("Step 2.1");
      //   setError("Token is missing. Please log in again.");
      //   return null;
      // }

      setIsLoading(true);
      setError(null);
      setSuccess(false);

      try {
        const data = await setTransactionDecision(
          token,
          transactionUuid,
          decision,
          inspectedPhysicalPassport,
          reasonForInspectedPhysicalPassport,
          riskAssessmentResult
        );
        setSuccess(true);
        return data;
      } catch (err) {
        setError(
          err.message || "An error occurred while updating the transaction."
        );
        console.error("Transaction update error:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  return { updateTransaction, isLoading, error, success };
};

const DocumentDetailsSection = ({ title, documentData }) => (
  <Accordion>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography>{title}</Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Grid container spacing={2}>
        {documentData &&
          Object.entries(documentData).map(([key, value]) => (
            <Grid item xs={6} key={key}>
              <Typography variant="body2" color="text.secondary">
                {key
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase())}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  wordWrap: "break-word",
                  wordBreak: "break-all",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%", // Adjust width if necessary
                }}
              >
                {value || "N/A"}
              </Typography>
            </Grid>
          ))}
      </Grid>
    </AccordionDetails>
  </Accordion>
);

const BiometricsSection = ({ biometrics }) => (
  <Accordion>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography>Biometrics</Typography>
    </AccordionSummary>
    <AccordionDetails>
      {biometrics &&
        biometrics.map((bio, index) => {
          const { biometricReference, biometricCaptured, ...otherFields } = bio;
          return (
            <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
              {Object.entries(otherFields).map(([key, value]) => (
                <Grid item xs={6} key={key}>
                  <Typography variant="body2" color="text.secondary">
                    {key
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase())}
                  </Typography>
                  <Typography variant="body1">{value || "N/A"}</Typography>
                </Grid>
              ))}
              {biometricReference && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Biometric Reference
                  </Typography>
                  <img
                    src={`data:image/jpeg;base64,${biometricReference}`}
                    alt="Biometric Reference"
                    style={{
                      width: "100%",
                      height: "auto",
                      maxHeight: "200px",
                      objectFit: "contain",
                      borderRadius: "8px",
                    }}
                  />
                </Grid>
              )}
              {biometricCaptured && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Biometric Captured
                  </Typography>
                  <img
                    src={`data:image/jpeg;base64,${biometricCaptured}`}
                    alt="Biometric Captured"
                    style={{
                      width: "100%",
                      height: "auto",
                      maxHeight: "200px",
                      objectFit: "contain",
                      borderRadius: "8px",
                    }}
                  />
                </Grid>
              )}
            </Grid>
          );
        })}
    </AccordionDetails>
  </Accordion>
);

const BehaviourBiometricsSection = ({ behaviour }) => {
  const formatConfidenceValue = (value) => {
    const numValue = parseFloat(value);
    return (numValue * 100).toFixed(1);
  };
  const [open, setOpen] = useState(false);
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>Behaviour Biometrics</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Status
            </Typography>
            <Typography variant="body1">{behaviour.status || "N/A"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Authenticated
            </Typography>
            <Typography variant="body1">
              {behaviour.authenticated ? "Yes" : "No" || "N/A"}
            </Typography>
          </Grid>
        </Grid>

        {behaviour.metadata?.features && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Confidence Metrics
            </Typography>
            <Grid container spacing={2}>
              {behaviour.metadata.features.map((feature) => (
                <Grid item xs={6} key={feature.name}>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {feature.name
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </Typography>
                    <Typography variant="body1">
                      {formatConfidenceValue(feature.value)}%
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            {behaviour.shapImage && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  XAI - Shap Values
                </Typography>
                <img
                  src={`data:image/jpeg;base64,${behaviour.shapImage}`}
                  alt="Biometric Shap Values"
                  style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "400px",
                    objectFit: "contain",
                    borderRadius: "8px",
                  }}
                  onClick={() => setOpen(true)}
                />
              </Grid>
            )}
            {/* {behaviour.limeImage && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  XAI - Lime Values
                </Typography>
                <img
                  src={`data:image/jpeg;base64,${behaviour.limeImage}`}
                  alt="Biometric Shap Values"
                  style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "400px",
                    objectFit: "contain",
                    borderRadius: "8px",
                  }}
                  onClick={() => setOpen(true)}
                />
              </Grid>
            )}
            {behaviour.limeConditionsImage && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  XAI - Lime Conditions
                </Typography>
                <img
                  src={`data:image/jpeg;base64,${behaviour.limeConditionsImage}`}
                  alt="Biometric Shap Values"
                  style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "400px",
                    objectFit: "contain",
                    borderRadius: "8px",
                  }}
                  onClick={() => setOpen(true)}
                />
              </Grid>
            )} */}
          </Box>
        )}

        {/* Modal for enlarged image */}
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogContent sx={{ display: "flex", justifyContent: "center" }}>
            <img
              src={`data:image/jpeg;base64,${behaviour?.shapImage}`}
              alt="Biometric Shap Values"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "90vh",
                objectFit: "contain",
              }}
            />
          </DialogContent>
        </Dialog>
      </AccordionDetails>
    </Accordion>
  );
};

const ValidationStatusSection = ({ validationStatus }) => (
  <Accordion>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography>Validation Status</Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Grid container spacing={2}>
        {validationStatus &&
          Object.entries(validationStatus).map(([key, value]) => (
            <Grid item xs={6} key={key}>
              <Typography variant="body2" color="text.secondary">
                {key
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase())}
              </Typography>
              <Typography variant="body1">
                {value ? "Passed" : "Failed"}
              </Typography>
            </Grid>
          ))}
      </Grid>
    </AccordionDetails>
  </Accordion>
);

const BackgroundChecksSection = ({ backgroundChecks }) => {
  if (!backgroundChecks) {
    return null;
  }

  const {
    person,
    items,
    events,
    alerts,
    decision,
    system,
    result,
    responsetime,
    personid,
    objectList,
    event_id,
  } = backgroundChecks;

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>Background Checks</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {/* System Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            System Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Result
              </Typography>
              <Typography variant="body1">{result || "N/A"}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Response Time
              </Typography>
              <Typography variant="body1">
                {responsetime ? new Date(responsetime).toLocaleString() : "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Systems Checked
              </Typography>
              <Typography variant="body1">
                {system ? system.join(", ") : "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Person ID
              </Typography>
              <Typography variant="body1">{personid || "N/A"}</Typography>
            </Grid>
            {event_id && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Event ID
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
                  {event_id}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Decision Information */}
        {decision && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Decision
            </Typography>
            <Box
              sx={{
                p: 2,
                border: "1px solid",
                borderColor:
                  decision.status === "REFUSED" ? "error.main" : "success.main",
                borderRadius: 1,
                backgroundColor:
                  decision.status === "REFUSED"
                    ? "error.lighter"
                    : "success.lighter",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color:
                        decision.status === "REFUSED"
                          ? "error.dark"
                          : "success.dark",
                    }}
                  >
                    {decision.status}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Reason
                  </Typography>
                  <Typography variant="body1">
                    {decision.reason?.replace(/_/g, " ") || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Decision Date
                  </Typography>
                  <Typography variant="body1">
                    {decision.decisionDate
                      ? new Date(decision.decisionDate).toLocaleDateString()
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Decision ID
                  </Typography>
                  <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
                    {decision.decisionId || "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}

        {/* Alerts Information */}
        {alerts && alerts.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Alerts ({alerts.length})
            </Typography>
            {alerts.map((alert, index) => (
              <Box key={alert.id || index} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "error.main",
                    borderRadius: 1,
                    backgroundColor: "error.lighter",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="error.dark"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    {alert.alertCategory} -{" "}
                    {alert.alertType?.replace(/_/g, " ").toUpperCase()}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Issuing Member State
                      </Typography>
                      <Typography variant="body1">
                        {alert.issuingMemberState || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Status
                      </Typography>
                      <Typography variant="body1">
                        {alert.status || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Date
                      </Typography>
                      <Typography variant="body1">
                        {alert.date
                          ? new Date(alert.date).toLocaleString()
                          : "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Alert ID
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ wordBreak: "break-all", fontSize: "0.875rem" }}
                      >
                        {alert.id || "N/A"}
                      </Typography>
                    </Grid>
                    {alert.legalBasis && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Legal Basis
                        </Typography>
                        <Typography variant="body1">
                          {alert.legalBasis}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Reason
                      </Typography>
                      <Typography variant="body1">
                        {alert.reason || "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Person Information - Keep existing if present */}
        {person && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Person Details
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(person).map(([key, value]) => (
                <Grid item xs={6} key={key}>
                  <Typography variant="body2" color="text.secondary">
                    {key
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase())}
                  </Typography>
                  <Typography variant="body1">{value || "N/A"}</Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Documents/Items Information - Keep existing if present */}
        {items && items.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Items
            </Typography>
            {items.map((item, index) => (
              <Box key={index}>
                <Box
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: index < items.length - 1 ? 2 : 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {item.type.toUpperCase()}
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(item.description).map(([key, value]) => (
                      <Grid item xs={6} key={key}>
                        <Typography variant="body2" color="text.secondary">
                          {key
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (str) => str.toUpperCase())}
                        </Typography>
                        <Typography variant="body1">
                          {value || "N/A"}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Events Information - Keep existing if present */}
        {events && events.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Events
            </Typography>
            {events.map((event, index) => (
              <Box key={index}>
                <Box
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor:
                      event.type === "alert" ? "error.main" : "divider",
                    borderRadius: 1,
                    backgroundColor:
                      event.type === "alert"
                        ? "error.lighter"
                        : "background.paper",
                    mb: index < events.length - 1 ? 2 : 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {event.type.toUpperCase()}
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(event.description).map(([key, value]) => (
                      <Grid item xs={12} key={key}>
                        <Typography variant="body2" color="text.secondary">
                          {key
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (str) => str.toUpperCase())}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color:
                              event.type === "alert"
                                ? "error.dark"
                                : "text.primary",
                          }}
                        >
                          {value || "N/A"}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Object List - if present */}
        {objectList && objectList.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Related Objects
            </Typography>
            <Grid container spacing={1}>
              {objectList.map((objectId, index) => (
                <Grid item xs={12} key={index}>
                  <Typography variant="body2" color="text.secondary">
                    Object {index + 1}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ wordBreak: "break-all", fontSize: "0.875rem" }}
                  >
                    {objectId}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const RiskAssessmentSection = ({ riskAssessment }) => {
  if (!riskAssessment) {
    return null;
  }

  const {
    riskScore,
    riskLevel,
    riskFactors,
    // recommendations,
    timestamp,
    assessmentDetails,
  } = riskAssessment;

  const getRiskLevelColor = (level) => {
    switch (level?.toUpperCase()) {
      case "LOW":
        return {
          color: "success.dark",
          bgcolor: "success.lighter",
          borderColor: "success.main",
          chipColor: "success",
        };
      case "MEDIUM":
        return {
          color: "warning.dark",
          bgcolor: "warning.lighter",
          borderColor: "warning.main",
          chipColor: "warning",
        };
      case "HIGH":
        return {
          color: "error.dark",
          bgcolor: "error.lighter",
          borderColor: "error.main",
          chipColor: "error",
        };
      case "CRITICAL":
        return {
          color: "error.dark",
          bgcolor: "error.lighter",
          borderColor: "error.main",
          chipColor: "error",
        };
      default:
        return {
          color: "text.primary",
          bgcolor: "grey.100",
          borderColor: "grey.300",
          chipColor: "default",
        };
    }
  };

  const formatRiskFactorName = (name) => {
    return name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatRiskScore = (score) => {
    return (score * 100).toFixed(1);
  };

  const riskLevelColors = getRiskLevelColor(riskLevel);

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>Risk Assessment</Typography>
        {typeof riskScore === "number" && (
          <Chip
            label={`Score: ${formatRiskScore(riskScore)}%`}
            size="small"
            color={riskLevelColors.chipColor}
            sx={{ ml: 2, fontWeight: 600 }}
          />
        )}
      </AccordionSummary>
      <AccordionDetails>
        {/* Overall Risk Summary */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Risk Summary
          </Typography>
          <Box
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: riskLevelColors.borderColor,
              borderRadius: 1,
              backgroundColor: riskLevelColors.bgcolor,
              mb: 2,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Risk Level
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: riskLevelColors.color,
                  }}
                >
                  {riskLevel}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Risk Score
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: riskLevelColors.color,
                  }}
                >
                  {formatRiskScore(riskScore)}%
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Assessment Time
                </Typography>
                <Typography variant="body1">
                  {timestamp ? new Date(timestamp).toLocaleString() : "N/A"}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
        {/* Risk Factors */}
        {riskFactors && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Risk Factors
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(riskFactors).map(([factor, score]) => {
                const percentage = formatRiskScore(score);
                const isHighRisk = score > 0.7;
                const isMediumRisk = score > 0.4 && score <= 0.7;

                return (
                  <Grid item xs={12} sm={6} key={factor}>
                    <Box
                      sx={{
                        p: 1.5,
                        border: "1px solid",
                        borderColor: isHighRisk
                          ? "error.main"
                          : isMediumRisk
                          ? "warning.main"
                          : "success.main",
                        borderRadius: 1,
                        backgroundColor: isHighRisk
                          ? "error.lighter"
                          : isMediumRisk
                          ? "warning.lighter"
                          : "success.lighter",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {formatRiskFactorName(factor)}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: isHighRisk
                            ? "error.dark"
                            : isMediumRisk
                            ? "warning.dark"
                            : "success.dark",
                        }}
                      >
                        {percentage}%
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* Recommendations */}
        {/* {recommendations && recommendations.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Recommendations ({recommendations.length})
            </Typography>
            {recommendations.map((recommendation, index) => (
              <Box
                key={index}
                sx={{
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "info.main",
                  borderRadius: 1,
                  backgroundColor: "info.lighter",
                  mb: index < recommendations.length - 1 ? 1 : 0,
                }}
              >
                <Typography variant="body2" color="info.dark">
                  • {recommendation}
                </Typography>
              </Box>
            ))}
          </Box>
        )} */}

        {/* Assessment Details */}
        {assessmentDetails && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Assessment Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Assessment Version
                </Typography>
                <Typography variant="body1">
                  {assessmentDetails.assessment_version || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Historical Crossings
                </Typography>
                <Typography variant="body1">
                  {assessmentDetails.historical_crossings_count || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Total Historical Records
                </Typography>
                <Typography variant="body1">
                  {assessmentDetails.total_historical_records || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Highest Risk Factor
                </Typography>
                <Typography variant="body1">
                  {assessmentDetails.highest_risk_factor
                    ? `${formatRiskFactorName(
                        assessmentDetails.highest_risk_factor[0]
                      )} (${formatRiskScore(
                        assessmentDetails.highest_risk_factor[1]
                      )}%)`
                    : "N/A"}
                </Typography>
              </Grid>

              {/* Person Identifier */}
              {/* {assessmentDetails.person_identifier && (
                <>
                  <Grid item xs={12}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, mb: 1 }}
                    >
                      Person Identifier
                    </Typography>
                  </Grid>
                  {Object.entries(assessmentDetails.person_identifier).map(
                    ([key, value]) => (
                      <Grid item xs={6} key={key}>
                        <Typography variant="body2" color="text.secondary">
                          {formatRiskFactorName(key)}
                        </Typography>
                        <Typography variant="body1">
                          {value || "N/A"}
                        </Typography>
                      </Grid>
                    )
                  )}
                </>
              )} */}

              {/* Risk Factor Weights */}
              {/* {assessmentDetails.risk_factor_weights && (
                <>
                  <Grid item xs={12}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2, mb: 1 }}
                    >
                      Risk Factor Weights
                    </Typography>
                  </Grid>
                  {Object.entries(assessmentDetails.risk_factor_weights).map(
                    ([factor, weight]) => (
                      <Grid item xs={6} key={factor}>
                        <Typography variant="body2" color="text.secondary">
                          {formatRiskFactorName(factor)}
                        </Typography>
                        <Typography variant="body1">
                          {(weight * 100).toFixed(0)}%
                        </Typography>
                      </Grid>
                    )
                  )}
                </>
              )} */}

              {assessmentDetails.transaction_uuid && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Transaction UUID
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ wordBreak: "break-all", fontSize: "0.875rem" }}
                  >
                    {assessmentDetails.transaction_uuid}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const TransactionCard = ({ transaction, token, onUpdate }) => {
  const navigate = useNavigate();
  const { updateTransaction, isLoading, error, success } =
    useTransactionUpdate(token);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nextStatus, setNextStatus] = useState(null);
  const [inspectedPhysicalPassport, setInspectedPhysicalPassport] =
    useState("");
  const [
    reasonForInspectedPhysicalPassport,
    setReasonForInspectedPhysicalPassport,
  ] = useState("");

  if (!transaction) {
    return (
      <Typography variant="body1" color="text.secondary" align="center">
        Transaction data is unavailable.
      </Typography>
    );
  }

  const handleUpdateClick = (status) => {
    setNextStatus(status);
    setDialogOpen(true);
  };

  const handleDialogSubmit = async () => {
    const passportInspected =
      inspectedPhysicalPassport.toLowerCase() === "true";
    const riskScore = transaction.riskAssessment?.riskScore ?? null;

    const result = await updateTransaction(
      transaction.transaction.transactionUuid,
      nextStatus,
      passportInspected,
      reasonForInspectedPhysicalPassport,
      riskScore
    );

    if (result) {
      setOpenSnackbar(true);
      onUpdate && onUpdate(transaction.transaction.transactionUuid, nextStatus);
    }

    setDialogOpen(false);
    setInspectedPhysicalPassport("");
    setReasonForInspectedPhysicalPassport("");

    if (nextStatus === "CLOSED") {
      if (isMobileApp()) {
        window.location.href = "odysseus-po://#";
      } else {
        navigate("/");
      }
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Transaction Status:{" "}
          <strong>{transaction.transaction.transactionStatus}</strong>
        </Typography>

        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Typography color="text.secondary" variant="caption">
              Traveler ID
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {transaction.transaction.travelerId}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography color="text.secondary" variant="caption">
              Travel ID
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {transaction.transaction.travelId}
            </Typography>
          </Grid>
        </Grid>

        <RiskAssessmentSection riskAssessment={transaction.riskAssessment} />

        <DocumentDetailsSection
          title="DTV-VC"
          documentData={transaction.dtoVC}
        />
        <DocumentDetailsSection
          title="DTC-PC"
          documentData={transaction.dtoPC}
        />
        <DocumentDetailsSection
          title="EMRTD"
          documentData={transaction.emrtd}
        />
        <BiometricsSection biometrics={transaction.biometrics} />
        <BehaviourBiometricsSection
          behaviour={transaction.behaviourBiometrics}
        />
        <BackgroundChecksSection
          backgroundChecks={transaction.backgroundChecks}
        />

        {/* Historical Transactions - NEW SECTION */}
        <HistoricalTransactionsSection
          historicalTransactions={transaction.historicalTransactions}
        />

        <ValidationStatusSection
          validationStatus={transaction.validationStatus}
        />

        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            align="center"
            sx={{ mb: 2 }}
          >
            Update Transaction Status
          </Typography>
          <Grid container spacing={2}>
            {[
              {
                status: "DTC_PC_VERIFICATION_REQUIRED",
                color: "primary",
              },
              {
                status: "EMRTD_VERIFICATION_REQUIRED",
                color: "primary",
              },
              {
                status: "DETAILED_VERIFICATION_NEEDED",
                color: "primary",
              },
              {
                status: "CLOSED",
                color: "error",
              },
            ].map(({ status, color }) => (
              <Grid item xs={12} sm={6} md={3} key={status}>
                <Button
                  variant="contained"
                  color={color}
                  onClick={() => handleUpdateClick(status)}
                  disabled={isLoading}
                  fullWidth
                  sx={{ height: 66 }}
                >
                  {status.replace(/_/g, " ").toUpperCase()}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </CardContent>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={isLoading ? "info" : success ? "success" : "error"}
          sx={{ width: "100%" }}
        >
          {isLoading
            ? "Updating transaction..."
            : success
            ? "Transaction updated successfully!"
            : error || "Failed to update transaction"}
        </Alert>
      </Snackbar>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            Passport Inspection Information
          </Typography>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">
              Was the physical passport inspected?
            </Typography>
            <TextField
              select
              value={inspectedPhysicalPassport}
              onChange={(e) => setInspectedPhysicalPassport(e.target.value)}
              fullWidth
              sx={{ mt: 1 }}
            >
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </TextField>
          </Box>

          {inspectedPhysicalPassport.toLowerCase() === "true" && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1">
                Reason for physical passport inspection
              </Typography>
              <TextField
                multiline
                rows={3}
                placeholder="Enter reason"
                value={reasonForInspectedPhysicalPassport}
                onChange={(e) =>
                  setReasonForInspectedPhysicalPassport(e.target.value)
                }
                fullWidth
                sx={{ mt: 1 }}
              />
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
            <Button onClick={() => setDialogOpen(false)} sx={{ mr: 2 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDialogSubmit}
              disabled={
                inspectedPhysicalPassport === "" ||
                (inspectedPhysicalPassport.toLowerCase() === "true" &&
                  reasonForInspectedPhysicalPassport.trim() === "")
              }
            >
              Update
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const TransactionWithDetails = () => {
  const { uuid } = useParams();
  // const dispatch = useDispatch();
  const [transactionData, setTransactionData] = useState(null);
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // const storedUsername =
  //   localStorage.getItem("username") || sessionStorage.getItem("username");
  // const storedPassword =
  //   localStorage.getItem("password") || sessionStorage.getItem("password");

  // useEffect(() => {
  //   const fetchTokenAndTransaction = async () => {
  //     setIsLoading(true);
  //     try {
  //       if (storedUsername && storedPassword) {
  //         const fetchedToken = await dispatch(
  //           fetchAccessToken({
  //             username: storedUsername,
  //             password: storedPassword,
  //           })
  //         ).unwrap();
  //         if (fetchedToken) {
  //           setToken(fetchedToken);
  //           const transaction = await getSpecificTransaction(
  //             fetchedToken,
  //             uuid,
  //             true
  //           );
  //           setTransactionData(transaction);
  //         }
  //       }
  //     } catch (err) {
  //       setError(err.message || "Failed to fetch data");
  //       console.error(err);
  //     } finally {
  //       setIsLoading(false);
  //     }
  //   };

  //   fetchTokenAndTransaction();
  // }, [uuid, storedUsername, storedPassword, dispatch]);

  const fetchTokenAndTransaction = useCallback(
    async (isInitialLoad = false) => {
      try {
        if (isInitialLoad) setIsLoading(true);

        // No need to fetch token, just call the service
        const transaction = await getSpecificTransaction(uuid, true);
        setTransactionData(transaction);
      } catch (err) {
        setError(err.message || "Failed to fetch data");
        console.error(err);
      } finally {
        if (isInitialLoad) setIsLoading(false);
      }
    },
    [uuid]
  );

  useEffect(() => {
    // Fetch data once on component mount with loading state
    fetchTokenAndTransaction(true);

    // Fetch data at regular intervals without loading state
    // const intervalId = setInterval(() => fetchTokenAndTransaction(), 5000);

    // return () => clearInterval(intervalId); // Cleanup interval on unmount
  }, [fetchTokenAndTransaction]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {isLoading ? (
        <Box sx={{ mt: 2, pl: 10, pr: 10 }}>
          <TableSkeleton />
        </Box>
      ) : error ? (
        <Typography variant="body1" color="error" align="center">
          {error}
        </Typography>
      ) : (
        transactionData && (
          <TransactionCard
            transaction={transactionData}
            token={token}
            onUpdate={(uuid, newStatus) => {
              setTransactionData((prev) =>
                prev.transaction.transactionUuid === uuid
                  ? {
                      ...prev,
                      transaction: {
                        ...prev.transaction,
                        transactionStatus: newStatus,
                      },
                    }
                  : prev
              );
            }}
          />
        )
      )}
    </Container>
  );
};

export default TransactionWithDetails;
